import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockClockIcon from '@mui/icons-material/LockClock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import dmdGoldFull from '../../assets/dmd/dmd-gold-fa.svg';
import integrityLogo from '../../assets/dmd/integrity-navy.svg';
import {
  ALLIANCE_APP_ORDER,
  ALLIANCE_APPS,
  AllianceAppKey,
} from './alliance-apps';
import { pageSurface } from './alliance-surface';
import { ALLIANCE_THRESHOLDS, DMD_BRAND } from './alliance-tokens';
import { APP_ICONS } from './app-icons';
import { useIsMobileLayout } from './bar-controls';
import { CURRENT_APP, CURRENT_APP_FONTS } from './current-app';

/**
 * Sign In page, layout D (DMD App System, recommended): a 44% navy brand
 * panel with the gold full lockup beside the sign-in column. Phones get a
 * 176px navy band instead. No photo, quote, social links or Sign up: new
 * agents register through their upline's link, so the page says so.
 */

/** Headline (with one emphasized phrase) and subline per app */
const TAGLINES: Record<
  AllianceAppKey,
  { before: string; em: string; after: string; sub: string }
> = {
  dashboard: {
    before: 'Your agency, ',
    em: 'at a glance',
    after: '.',
    sub: 'Commissions, production and your team in one place.',
  },
  contracting: {
    before: 'From registration to your first ',
    em: 'writing number',
    after: '.',
    sub: 'Agreements, licenses, carrier contracts and your wallet.',
  },
};

/** The emphasized phrase: a darker DMD gold that reads on #f1f1f1 */
const EMPHASIS = '#826845';

/** Width / height of the full DMD lockups (dmd-*-fa.svg, 1871×640) */
const FULL_LOCKUP_RATIO = 1871 / 640;

/**
 * @param onSignIn - Starts the Hosted UI redirect. If it throws, or the page
 *   is still here after 8s, the button shows "We couldn't start sign-in"
 * @param forgotPasswordHref - The Hosted UI forgot-password URL
 * @param notice - "expired" shows the calm session-expired notice
 */
export function SignInPage(props: {
  onSignIn: () => Promise<unknown> | void;
  forgotPasswordHref: string;
  notice?: 'expired' | null;
}) {
  // State
  const [status, setStatus] = React.useState<'idle' | 'pending' | 'failed'>(
    'idle',
  );
  // Ref
  const timerRef = React.useRef<number>();
  // Hooks
  const isMobile = useIsMobileLayout();

  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const c = pageSurface();
  const key = CURRENT_APP.key;
  const tagline = TAGLINES[key];
  const titleFont = CURRENT_APP_FONTS.title as React.CSSProperties;
  const AppIcon = APP_ICONS[key];
  const pending = status === 'pending';

  const handleSignIn = async () => {
    if (pending) {
      return;
    }
    setStatus('pending');
    window.clearTimeout(timerRef.current);
    // The redirect normally unloads the page; if it hasn't, it failed
    timerRef.current = window.setTimeout(
      () => setStatus('failed'),
      ALLIANCE_THRESHOLDS.signInFailureMs,
    );
    try {
      await props.onSignIn();
    } catch (error) {
      window.clearTimeout(timerRef.current);
      setStatus('failed');
    }
  };

  const actions = (
    <Box
      sx={{
        width: '100%',
        maxWidth: isMobile ? '100%' : 400,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 4 : '36px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: CURRENT_APP.tile.bg,
              border: '1px solid #ffffff1a',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: CURRENT_APP.tile.icon,
            }}
          >
            <AppIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: c.text2,
            }}
          >
            {CURRENT_APP.fullName}
          </Box>
        </Box>
        <Box
          component="h1"
          sx={{
            m: 0,
            ...titleFont,
            color: DMD_BRAND.heading,
            fontSize: isMobile ? 34 : 44,
            lineHeight: 1.12,
            letterSpacing: c.title.letterSpacing,
            textWrap: 'balance',
          }}
        >
          {tagline.before}
          <Box component="span" sx={{ color: EMPHASIS, fontWeight: 700 }}>
            {tagline.em}
          </Box>
          {tagline.after}
        </Box>
        <Box sx={{ fontSize: 16, lineHeight: 1.5, color: c.text2 }}>
          {tagline.sub}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {props.notice === 'expired' ? (
          <Box
            role="status"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              p: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${c.border}`,
              backgroundColor: '#0000000a',
              fontSize: 14,
              lineHeight: 1.45,
            }}
          >
            <LockClockIcon sx={{ fontSize: 20, color: c.text2 }} />
            <Box sx={{ flex: 1 }}>
              Your session expired. Please sign in again.
            </Box>
          </Box>
        ) : null}

        <ButtonBase
          onClick={handleSignIn}
          aria-disabled={pending}
          aria-busy={pending}
          sx={{
            fontFamily: 'inherit',
            height: 52,
            borderRadius: '14px',
            backgroundColor: c.primary.bg,
            color: c.primary.fg,
            justifyContent: 'space-between',
            p: '0 18px 0 20px',
            fontSize: 16,
            fontWeight: 500,
            opacity: pending ? 0.75 : 1,
            cursor: pending ? 'progress' : 'pointer',
            '&:hover': { filter: 'brightness(1.08)' },
            '&.Mui-focusVisible': {
              outline: `2px solid ${DMD_BRAND.progress}`,
              outlineOffset: 2,
            },
          }}
        >
          {pending ? 'Opening sign-in…' : 'Sign in'}
          {pending ? (
            <CircularProgress
              size={18}
              thickness={5}
              sx={{ color: c.primary.fg }}
            />
          ) : (
            <ArrowForwardIcon sx={{ fontSize: 20 }} />
          )}
        </ButtonBase>

        {status === 'failed' ? (
          <Box
            role="alert"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 14,
              lineHeight: 1.45,
              color: '#c62828',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18, mt: '1px' }} />
            <div>We couldn’t start sign-in. Please try again.</div>
          </Box>
        ) : null}

        <Box
          component="a"
          href={props.forgotPasswordHref}
          sx={{
            alignSelf: 'flex-start',
            fontSize: 14,
            fontWeight: 500,
            color: c.link,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            textDecorationColor: c.border2,
          }}
        >
          Forgot password?
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          pt: 3,
          borderTop: `1px solid ${c.border}`,
          fontSize: 14,
          lineHeight: 1.5,
          color: c.text2,
        }}
      >
        <PersonAddIcon sx={{ fontSize: 20, mt: '1px' }} />
        <Box sx={{ textWrap: 'pretty' }}>
          New to DMD? Ask your upline for their registration link to get
          started.
        </Box>
      </Box>
    </Box>
  );

  // The two DMD apps (people mix up the URLs), and the Integrity logo
  const footer = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box
        component="nav"
        aria-label="DMD apps"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 16px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
        }}
      >
        {ALLIANCE_APP_ORDER.filter((app) => !isMobile || app !== key).map(
          (app) => (
            <Box
              key={app}
              component="a"
              href={ALLIANCE_APPS[app].url}
              sx={{
                color: app === key ? c.text : c.text2,
                textDecoration: 'none',
                transition: 'color 0.15s',
                '&:hover': { color: app === key ? c.text : c.link },
              }}
            >
              {ALLIANCE_APPS[app].name}
            </Box>
          ),
        )}
      </Box>
      <Box
        component="img"
        src={integrityLogo}
        alt="Integrity"
        sx={{
          display: 'block',
          flex: 'none',
          height: isMobile ? 16 : 20,
          width: isMobile ? 112 : 140,
        }}
      />
    </Box>
  );

  const lockup = (height: number) => (
    <Box
      component="img"
      src={dmdGoldFull}
      alt="DMD Financial Agency"
      sx={{
        display: 'block',
        height,
        width: Math.round(height * FULL_LOCKUP_RATIO),
      }}
    />
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          backgroundColor: c.bg,
          color: c.text,
          letterSpacing: 'normal',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            height: 176,
            flex: 'none',
            backgroundColor: DMD_BRAND.navy,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {lockup(60)}
        </Box>
        <Box
          component="main"
          sx={{
            flex: 1,
            // 96px at the bottom clears the CookieYes and Freshchat buttons
            p: '32px 24px 96px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Box sx={{ flex: 1 }}>{actions}</Box>
          {footer}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: 600,
        backgroundColor: c.bg,
        color: c.text,
        letterSpacing: 'normal',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '44%',
          flex: 'none',
          backgroundColor: DMD_BRAND.navy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '56px',
          boxSizing: 'border-box',
        }}
      >
        {lockup(96)}
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          // 96px at the bottom clears the CookieYes and Freshchat buttons
          p: '40px 56px 96px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {actions}
        </Box>
        {footer}
      </Box>
    </Box>
  );
}
